/**
 * F09 v2 (E9 / E23) — the ID-document inputs shared by the Approve dialog and
 * the Direct-verify page.
 *
 * The ID number is a masked (password-style) input with a reveal toggle. It
 * lives ONLY in the parent component's state for the life of the dialog/page:
 * it is never logged, never written to storage, never put in a URL and never
 * echoed in an error. Autofill / password managers are told to ignore it.
 */
import { useId, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Button, FormField, Select, TextInput } from '@bravemove/ui-web';
import { ID_DOCUMENT_TYPES, VERIFICATION_ID_NUMBER_MAX } from '@bravemove/types';
import { isIdDocumentType, type IdDocumentErrors, type IdDocumentValues } from './idDocument';
import { idTypeLabel } from './labels';

export function IdDocumentFields({
  values,
  onChange,
  errors = {},
  required,
  testIdPrefix,
}: {
  readonly values: IdDocumentValues;
  readonly onChange: (next: IdDocumentValues) => void;
  readonly errors?: IdDocumentErrors;
  readonly required: boolean;
  readonly testIdPrefix: string;
}) {
  const { t } = useI18n();
  const [revealed, setRevealed] = useState(false);
  const numberId = useId();
  const typeId = useId();
  const countryId = useId();

  const typeOptions = ID_DOCUMENT_TYPES.map((v) => ({ value: v, label: idTypeLabel(t, v) }));

  return (
    <div className="space-y-3">
      <FormField
        label={t('admin:verification.approve.idNumber')}
        helper={t('admin:verification.approve.idNumberHelper')}
        error={errors.number}
        required={required}
        htmlFor={numberId}
      >
        <TextInput
          id={numberId}
          type={revealed ? 'text' : 'password'}
          value={values.number}
          maxLength={VERIFICATION_ID_NUMBER_MAX}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          invalid={!!errors.number}
          data-testid={`${testIdPrefix}-id-number`}
          onChange={(e) => onChange({ ...values, number: e.target.value })}
          trailing={
            <Button
              type="button"
              variant="ghost"
              size="xs"
              aria-pressed={revealed}
              data-testid={`${testIdPrefix}-id-reveal`}
              onClick={() => setRevealed((r) => !r)}
            >
              {revealed
                ? t('admin:verification.approve.hide')
                : t('admin:verification.approve.reveal')}
            </Button>
          }
        />
      </FormField>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField
          label={t('admin:verification.approve.idType')}
          error={errors.type}
          required={required}
          htmlFor={typeId}
        >
          <Select
            id={typeId}
            value={values.type}
            options={typeOptions}
            placeholder={t('admin:verification.approve.idTypePlaceholder')}
            invalid={!!errors.type}
            data-testid={`${testIdPrefix}-id-type`}
            onChange={(e) => {
              const v = e.target.value;
              onChange({ ...values, type: isIdDocumentType(v) ? v : '' });
            }}
          />
        </FormField>
        <FormField
          label={t('admin:verification.approve.country')}
          helper={t('admin:verification.approve.countryHelper')}
          error={errors.country}
          required={required}
          htmlFor={countryId}
        >
          <TextInput
            id={countryId}
            value={values.country}
            maxLength={2}
            autoComplete="off"
            invalid={!!errors.country}
            data-testid={`${testIdPrefix}-id-country`}
            onChange={(e) => onChange({ ...values, country: e.target.value.toUpperCase() })}
          />
        </FormField>
      </div>
    </div>
  );
}
